import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  inject,
  HostListener,
  DestroyRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { QuillModule } from 'ngx-quill';
import {
  CoinsingReleaseService,
  CoinSingReleaseOutcome,
  CoinSingDeviceResult
} from '../../services/coinsing-release.service';
import {
  compareVersions,
  normalizeInstalledVersion,
  VersionCatalogEntry
} from '../../utils/coinsing-version.util';
import {
  CONFIRM_HINT_TEXT,
  ConfirmHintState,
  confirmHintState,
  computeDeleteImpact,
  DELETE_COPY,
  DeleteDeviceRef,
  DeleteImpact,
  deviceLabel,
  environmentBadge,
  EnvBadge,
  formatBytes,
  isDeleteConfirmMatch,
  mapDeleteError,
  ONLY_VERSION_WARNING,
  runningWarningText
} from '../../utils/coinsing-delete-release.util';

export interface KonoboxTargetDevice {
  deviceId: string;
  deviceName?: string;
  appVersion?: string;
  isTesting?: boolean;
  deviceOnline?: boolean;
  [key: string]: any;
}

@Component({
  selector: 'app-konobox-app-versions-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, QuillModule],
  templateUrl: './konobox-app-versions-modal.component.html',
  styleUrls: ['./konobox-app-versions-modal.component.css']
})
export class KonoboxAppVersionsModalComponent implements OnInit {
  @Input() selectedDeviceIds: Set<string> = new Set();
  @Input() allDevices: KonoboxTargetDevice[] = [];
  @Input() showTestMachines: boolean = false;
  @Input() canReleaseDevice: boolean = false;
  @Input() canReleaseAll: boolean = false;
  /** KAN-578: konobox-management_release-delete (superadmin implicit). The Delete action is hidden without it. */
  @Input() canDeleteRelease: boolean = false;

  @Output() close = new EventEmitter<void>();
  @Output() clearSelection = new EventEmitter<void>();
  @Output() released = new EventEmitter<CoinSingReleaseOutcome>();

  private releaseService = inject(CoinsingReleaseService);
  private sanitizer = inject(DomSanitizer);
  private destroyRef = inject(DestroyRef);
  private timers = new Set<ReturnType<typeof setTimeout>>();

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.timers.forEach(t => clearTimeout(t));
      this.timers.clear();
    });
  }

  private later(fn: () => void, ms = 0): void {
    const t = setTimeout(() => {
      this.timers.delete(t);
      fn();
    }, ms);
    this.timers.add(t);
  }

  isLoading = true;
  isReleasing = false;
  errorMessage = '';

  appVersionsList: VersionCatalogEntry[] = [];
  openDropdownVersion: string | null = null;

  // Confirmation dialogs
  showReleaseToAllDialog = false;
  showBulkRollbackDialog = false;

  pendingVersion: VersionCatalogEntry | null = null;
  pendingIsForce = false;
  pendingIsAll = false;

  // Confirm checkboxes
  confirmRollbackCheckbox = false;
  confirmForceCheckbox = false;

  // Computed counts for Release to All confirmation (read at confirm time)
  confirmTotalActiveCount = 0;
  confirmOfflineCount = 0;
  confirmTestMachineCount = 0;

  // Computed counts for Bulk Rollback
  bulkRollbackCount = 0;
  bulkUnknownDirectionCount = 0;
  bulkUpgradeCount = 0;

  // Result Panel State (AC-9)
  resultOutcome: CoinSingReleaseOutcome | null = null;
  showResultPanel = false;

  // Release Notes Drawer State
  showReleaseNoteDrawer = false;
  selectedNoteVersion: VersionCatalogEntry | null = null;
  isLoadingNote = false;
  isSavingNote = false;
  isEditingNote = false;
  releaseNoteContent = '';
  releaseNoteUpdatedAt?: string;
  releaseNoteUpdatedBy?: string;
  noteFeedbackMessage = '';
  noteErrorMessage = '';

  quillConfig = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ header: 1 }, { header: 2 }, { header: 3 }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      ['clean'],
      ['link']
    ]
  };

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.split-release-btn-group')) {
      this.openDropdownVersion = null;
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (this.showDeleteDialog) {
      // Escape is ignored while the call is in flight (destructive dialog).
      if (!this.isDeleting) this.closeDeleteDialog();
      event.stopPropagation();
      return;
    }
    if (this.isReleasing || this.isSavingNote || this.isDeleting) return;
    if (this.showReleaseToAllDialog || this.showBulkRollbackDialog) {
      this.cancelConfirmationDialogs();
      event.stopPropagation();
      return;
    }
    if (this.showReleaseNoteDrawer) {
      this.closeReleaseNotesDrawer();
      event.stopPropagation();
      return;
    }
    this.closeModal();
  }

  async ngOnInit(): Promise<void> {
    await this.loadVersions();
  }

  closeModal(): void {
    if (this.isReleasing || this.isDeleting) return;
    this.close.emit();
  }

  async loadVersions(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = '';
    try {
      this.appVersionsList = await this.releaseService.getAppReleases();
    } catch (err: any) {
      console.error('Error loading CoinSing versions:', err);
      this.errorMessage = 'Failed to load CoinSing versions. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  get selectedDeviceCount(): number {
    return this.selectedDeviceIds.size;
  }

  canReleaseThisVersion(ver: VersionCatalogEntry): boolean {
    if (!ver.isValid) return false;
    if (this.selectedDeviceCount > 0) {
      return this.canReleaseDevice;
    }
    return this.canReleaseDevice && this.canReleaseAll;
  }

  toggleDropdown(verId: string, event: Event): void {
    event.stopPropagation();
    this.openDropdownVersion = this.openDropdownVersion === verId ? null : verId;
  }

  onClearSelection(): void {
    this.clearSelection.emit();
  }

  // ---------------------------------------------------------------------------
  // Release Actions & Dialog Routing
  // ---------------------------------------------------------------------------
  initiateRelease(versionData: VersionCatalogEntry, force: boolean, scopeAll: boolean): void {
    this.openDropdownVersion = null;
    this.pendingVersion = versionData;
    this.pendingIsForce = force;
    this.pendingIsAll = scopeAll;
    this.confirmRollbackCheckbox = false;
    this.confirmForceCheckbox = false;

    if (scopeAll) {
      this.prepareReleaseToAllDialog(versionData);
      return;
    }

    // Selected Devices Mode: calculate rollback devices
    this.prepareSelectedRollbackCheck(versionData);
  }

  prepareReleaseToAllDialog(versionData: VersionCatalogEntry): void {
    // Read counts at confirm time (AC-6)
    let candidateDevices = this.allDevices.filter(d => !d['isDeleted']);
    if (!this.showTestMachines) {
      candidateDevices = candidateDevices.filter(d => !d.isTesting);
    }

    this.confirmTotalActiveCount = candidateDevices.length;
    this.confirmOfflineCount = candidateDevices.filter(d => !d.deviceOnline).length;
    this.confirmTestMachineCount = this.showTestMachines
      ? candidateDevices.filter(d => d.isTesting).length
      : 0;

    // Check if any device would be a rollback
    this.bulkRollbackCount = candidateDevices.filter(d => {
      const norm = normalizeInstalledVersion(d.appVersion);
      return norm ? compareVersions(versionData.version, norm) < 0 : false;
    }).length;

    this.showReleaseToAllDialog = true;
    setTimeout(() => document.getElementById('btnCancelReleaseToAll')?.focus(), 0);
  }

  prepareSelectedRollbackCheck(versionData: VersionCatalogEntry): void {
    const selectedDevices = this.allDevices.filter(d => this.selectedDeviceIds.has(d.deviceId) && !d['isDeleted']);
    let rollbacks = 0;
    let unknown = 0;
    let upgrades = 0;

    for (const dev of selectedDevices) {
      const norm = normalizeInstalledVersion(dev.appVersion);
      if (!norm) {
        unknown++;
      } else if (compareVersions(versionData.version, norm) < 0) {
        rollbacks++;
      } else {
        upgrades++;
      }
    }

    this.bulkRollbackCount = rollbacks;
    this.bulkUnknownDirectionCount = unknown;
    this.bulkUpgradeCount = upgrades;

    if (rollbacks > 0) {
      this.showBulkRollbackDialog = true;
      setTimeout(() => document.getElementById('btnCancelBulkRollback')?.focus(), 0);
    } else {
      void this.executeReleaseCall(false);
    }
  }

  cancelConfirmationDialogs(): void {
    this.showReleaseToAllDialog = false;
    this.showBulkRollbackDialog = false;
    this.pendingVersion = null;
    this.confirmRollbackCheckbox = false;
    this.confirmForceCheckbox = false;
  }

  get isReleaseToAllSubmitDisabled(): boolean {
    if (this.isReleasing) return true;
    if (this.pendingIsForce && !this.confirmForceCheckbox) return true;
    if (this.bulkRollbackCount > 0 && !this.confirmRollbackCheckbox) return true;
    return false;
  }

  confirmReleaseToAll(): void {
    this.showReleaseToAllDialog = false;
    void this.executeReleaseCall(this.bulkRollbackCount > 0);
  }

  confirmBulkRollback(): void {
    this.showBulkRollbackDialog = false;
    void this.executeReleaseCall(true);
  }

  async executeReleaseCall(rollbackConfirmed: boolean): Promise<void> {
    if (!this.pendingVersion || this.isReleasing) return;

    this.isReleasing = true;
    this.errorMessage = '';

    const ver = this.pendingVersion.version;
    const force = this.pendingIsForce;
    const isAll = this.pendingIsAll;

    try {
      let outcome: CoinSingReleaseOutcome;
      if (isAll) {
        outcome = await this.releaseService.releaseAppToAllDevices(
          ver,
          this.showTestMachines,
          force,
          rollbackConfirmed
        );
      } else {
        const ids = Array.from(this.selectedDeviceIds);
        outcome = await this.releaseService.releaseAppToSelectedDevices(
          ids,
          ver,
          force,
          rollbackConfirmed
        );
      }

      this.resultOutcome = outcome;
      this.showResultPanel = true;
      this.released.emit(outcome);
    } catch (err: any) {
      console.error('Release failed:', err);
      this.errorMessage = err.message || 'Failed to complete release action.';
    } finally {
      this.isReleasing = false;
      this.pendingVersion = null;
    }
  }

  // ---------------------------------------------------------------------------
  // Result Panel & Retry Failed (AC-9)
  // ---------------------------------------------------------------------------
  closeResultPanel(): void {
    this.showResultPanel = false;
  }

  get failedDeviceResults(): CoinSingDeviceResult[] {
    return (this.resultOutcome?.results || []).filter(r => r.result === 'failed');
  }

  get succeededDeviceResults(): CoinSingDeviceResult[] {
    return (this.resultOutcome?.results || []).filter(r => r.result === 'accepted');
  }

  async retryFailedDevices(): Promise<void> {
    if (!this.resultOutcome || this.isReleasing) return;
    const failedIds = this.failedDeviceResults.map(r => r.deviceId);
    if (failedIds.length === 0) return;

    this.isReleasing = true;
    this.errorMessage = '';

    try {
      const outcome = await this.releaseService.releaseAppToSelectedDevices(
        failedIds,
        this.resultOutcome.version,
        this.resultOutcome.force,
        true
      );

      // Merge outcome results with previous successes
      const previousSuccesses = this.succeededDeviceResults;
      this.resultOutcome = {
        ...outcome,
        results: [...previousSuccesses, ...outcome.results],
        counts: {
          accepted: previousSuccesses.length + outcome.counts.accepted,
          skipped: outcome.counts.skipped,
          failed: outcome.counts.failed,
          rollbacks: previousSuccesses.filter(s => s.isRollback).length + outcome.counts.rollbacks
        }
      };
      this.released.emit(this.resultOutcome);
    } catch (err: any) {
      console.error('Retry failed:', err);
      this.errorMessage = err.message || 'Failed to retry failed devices.';
    } finally {
      this.isReleasing = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Release Notes Drawer Logic (AC-7)
  // ---------------------------------------------------------------------------
  openReleaseNotes(versionData: VersionCatalogEntry): void {
    this.selectedNoteVersion = versionData;
    this.showReleaseNoteDrawer = true;
    void this.loadReleaseNoteForVersion(versionData.version);
  }

  closeReleaseNotesDrawer(): void {
    this.showReleaseNoteDrawer = false;
    this.selectedNoteVersion = null;
    this.isEditingNote = false;
    this.noteFeedbackMessage = '';
    this.noteErrorMessage = '';
  }

  async loadReleaseNoteForVersion(version: string): Promise<void> {
    this.isLoadingNote = true;
    this.isEditingNote = false;
    this.noteFeedbackMessage = '';
    this.noteErrorMessage = '';

    try {
      const verData = await this.releaseService.getAppReleaseVersion(version);
      if (verData) {
        this.releaseNoteContent = verData.releaseNote || '';
        this.releaseNoteUpdatedAt = verData.releaseNoteUpdatedAt;
        this.releaseNoteUpdatedBy = verData.releaseNoteUpdatedBy;
      } else {
        this.releaseNoteContent = '';
        this.releaseNoteUpdatedAt = undefined;
        this.releaseNoteUpdatedBy = undefined;
      }
    } catch (err: any) {
      console.error('Error loading release note:', err);
      this.noteErrorMessage = 'Failed to load release notes for this version.';
    } finally {
      this.isLoadingNote = false;
    }
  }

  startEditNote(): void {
    if (!this.canReleaseDevice) return;
    this.isEditingNote = true;
    this.noteFeedbackMessage = '';
    this.noteErrorMessage = '';
  }

  cancelEditNote(): void {
    this.isEditingNote = false;
    this.noteFeedbackMessage = '';
    this.noteErrorMessage = '';
    if (this.selectedNoteVersion) {
      void this.loadReleaseNoteForVersion(this.selectedNoteVersion.version);
    }
  }

  async saveReleaseNote(): Promise<void> {
    if (!this.selectedNoteVersion?.version || !this.canReleaseDevice) return;

    this.isSavingNote = true;
    this.noteFeedbackMessage = '';
    this.noteErrorMessage = '';

    try {
      await this.releaseService.saveAppReleaseNote(
        this.selectedNoteVersion.version,
        this.releaseNoteContent || ''
      );
      this.releaseNoteUpdatedAt = new Date().toISOString();
      this.noteFeedbackMessage = 'Release note saved successfully!';
      this.isEditingNote = false;

      // Update in local versions list
      const entry = this.appVersionsList.find(v => v.version === this.selectedNoteVersion?.version);
      if (entry) {
        entry.releaseNote = this.releaseNoteContent;
        entry.releaseNoteUpdatedAt = this.releaseNoteUpdatedAt;
      }
    } catch (err: any) {
      console.error('Error saving release note:', err);
      this.noteErrorMessage = `Failed to save release note: ${err.message || err}`;
    } finally {
      this.isSavingNote = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Delete a released version (KAN-578)
  // ---------------------------------------------------------------------------
  readonly deleteCopy = DELETE_COPY;
  readonly deleteOnlyWarning = ONLY_VERSION_WARNING;

  showDeleteDialog = false;
  deleteTarget: VersionCatalogEntry | null = null;
  deleteConfirmInput = '';
  isDeleting = false;
  /** Set after any failed attempt: the commit button reads "Retry delete". */
  deleteFailed = false;
  deleteErrorMessage = '';
  /** Blocked (or permission) state: input and Delete are not rendered, only Close. */
  deleteBlockedMessage = '';
  deleteBlockedDevices: DeleteDeviceRef[] = [];
  deleteImpact: DeleteImpact = { pending: [], downloading: [], runningCount: 0 };
  deleteOnlyVersion = false;
  deleteInputTouched = false;
  removingVersion: string | null = null;
  deleteSuccessMessage = '';
  deleteAuditWarning = '';
  deleteInfoMessage = '';
  private deleteTrigger: HTMLElement | null = null;

  /** Delete is offered for valid-grammar rows only (the callable rejects invalid versions) and needs the permission. */
  canShowDelete(ver: VersionCatalogEntry): boolean {
    return this.canDeleteRelease && ver.isValid;
  }

  isDeletingRow(ver: VersionCatalogEntry): boolean {
    return ver.deleteState === 'deleting';
  }

  get isDeleteBlocked(): boolean {
    return this.deleteBlockedMessage !== '';
  }

  get isDeleteFinishMode(): boolean {
    return !!this.deleteTarget && this.deleteTarget.deleteState === 'deleting';
  }

  get deleteConfirmState(): ConfirmHintState {
    return confirmHintState(this.deleteConfirmInput, this.deleteTarget?.version ?? '');
  }

  get deleteConfirmHintText(): string {
    return CONFIRM_HINT_TEXT[this.deleteConfirmState];
  }

  get deleteInputInvalid(): boolean {
    return this.deleteInputTouched && this.deleteConfirmState === 'mismatch';
  }

  get canSubmitDelete(): boolean {
    return !!this.deleteTarget
      && !this.isDeleting
      && !this.isDeleteBlocked
      && isDeleteConfirmMatch(this.deleteConfirmInput, this.deleteTarget.version);
  }

  get deleteEnvBadge(): EnvBadge {
    return environmentBadge(this.deleteTarget?.environment);
  }

  get deleteSizeText(): string {
    return formatBytes(this.deleteTarget?.sizeBytes);
  }

  get deletePublishedAt(): any {
    const p = this.deleteTarget?.publishedAt;
    if (!p) return null;
    return p.toDate ? p.toDate() : p;
  }

  get deleteRunningWarning(): string {
    return this.deleteTarget && this.deleteImpact.runningCount > 0
      ? runningWarningText(this.deleteTarget.version, this.deleteImpact.runningCount)
      : '';
  }

  deviceLabelOf(d: DeleteDeviceRef): string {
    return deviceLabel(d);
  }

  get visibleBlockedDevices(): DeleteDeviceRef[] {
    return this.deleteBlockedDevices.slice(0, 10);
  }

  get hiddenBlockedDeviceCount(): number {
    return Math.max(0, this.deleteBlockedDevices.length - 10);
  }

  get deleteCommitLabel(): string {
    if (this.isDeleting) return 'Deleting...';
    return this.deleteFailed || this.isDeleteFinishMode ? 'Retry delete' : 'Delete version';
  }

  openDeleteDialog(ver: VersionCatalogEntry, event?: Event): void {
    if (this.isDeleting || this.isReleasing || !this.canShowDelete(ver)) return;
    this.openDropdownVersion = null;
    this.deleteTrigger = (event?.currentTarget as HTMLElement) ?? null;
    this.deleteTarget = ver;
    this.deleteConfirmInput = '';
    this.deleteInputTouched = false;
    this.deleteFailed = false;
    this.deleteErrorMessage = '';
    this.deleteBlockedMessage = '';
    this.deleteBlockedDevices = [];
    this.deleteSuccessMessage = '';
    this.deleteAuditWarning = '';
    this.deleteInfoMessage = '';
    this.deleteImpact = computeDeleteImpact(ver.version, this.allDevices);
    this.deleteOnlyVersion = this.appVersionsList.filter(v => v.isValid).length === 1;

    // Client preview from the already-loaded device list; the server stays authoritative.
    if (this.deleteImpact.pending.length > 0) {
      const view = mapDeleteError(
        { details: { reason: 'pending_devices', devices: this.deleteImpact.pending } }, ver.version);
      this.setBlocked(view.message, view.devices);
    } else if (this.deleteImpact.downloading.length > 0) {
      const view = mapDeleteError(
        { details: { reason: 'download_in_progress', devices: this.deleteImpact.downloading } }, ver.version);
      this.setBlocked(view.message, view.devices);
    }

    this.showDeleteDialog = true;
    // Initial focus: Cancel (Close when blocked, same element id).
    this.later(() => this.focusById('btnCancelDelete'));
  }

  private setBlocked(message: string, devices: DeleteDeviceRef[]): void {
    this.deleteBlockedMessage = message;
    this.deleteBlockedDevices = devices;
    this.deleteErrorMessage = '';
  }

  closeDeleteDialog(): void {
    if (this.isDeleting) return;
    this.showDeleteDialog = false;
    this.deleteTarget = null;
    this.deleteConfirmInput = '';
    this.deleteErrorMessage = '';
    this.deleteBlockedMessage = '';
    this.deleteBlockedDevices = [];
    this.later(() => {
      const trigger = this.deleteTrigger;
      this.deleteTrigger = null;
      if (trigger && typeof document !== 'undefined' && document.contains(trigger)) {
        trigger.focus();
      } else {
        this.focusHeading();
      }
    });
  }

  onDeleteConfirmInput(): void {
    if (this.deleteConfirmInput.trim() !== '') this.deleteInputTouched = true;
  }

  /** Keeps Tab / Shift+Tab inside the dialog. */
  onDeleteDialogKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab' || typeof document === 'undefined') return;
    const box = event.currentTarget as HTMLElement;
    const items = Array.from(box.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), [tabindex="0"]'
    ));
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !box.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !box.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  }

  async submitDelete(): Promise<void> {
    if (this.isDeleting) return; // double-submit guard
    const target = this.deleteTarget;
    if (!target || this.isDeleteBlocked || !isDeleteConfirmMatch(this.deleteConfirmInput, target.version)) return;

    this.isDeleting = true; // synchronous, before the first await
    this.deleteErrorMessage = '';
    const version = target.version;

    try {
      const result = await this.releaseService.deleteRelease(version, this.deleteConfirmInput.trim());
      this.isDeleting = false;
      this.onDeleteSucceeded(version, result?.auditWritten !== false);
    } catch (err: any) {
      this.isDeleting = false;
      const view = mapDeleteError(err, version);
      console.error('Delete release failed:', view.reason); // reason code to console only, never to the user
      switch (view.kind) {
        case 'blocked':
        case 'permission':
          this.setBlocked(view.message, view.devices);
          this.later(() => this.focusById('btnCancelDelete'));
          break;
        case 'not_found':
          this.showDeleteDialog = false;
          this.deleteTarget = null;
          this.deleteInfoMessage = view.message;
          this.deleteSuccessMessage = '';
          void this.refreshQuietly();
          this.later(() => this.focusHeading());
          break;
        case 'retryable': {
          this.deleteFailed = true;
          this.deleteErrorMessage = view.message;
          const entry = this.appVersionsList.find(v => v.version === version);
          if (entry) entry.deleteState = 'deleting';
          this.later(() => this.focusById('deleteErrorRegion'));
          break;
        }
        default:
          this.deleteFailed = true;
          this.deleteErrorMessage = view.message;
          this.later(() => this.focusById('deleteErrorRegion'));
          break;
      }
    }
  }

  private onDeleteSucceeded(version: string, auditWritten: boolean): void {
    if (this.selectedNoteVersion?.version === version) this.closeReleaseNotesDrawer();
    this.showDeleteDialog = false;
    this.deleteTarget = null;
    this.deleteConfirmInput = '';
    this.deleteTrigger = null;
    this.deleteInfoMessage = '';
    this.removingVersion = version;

    const reduced = typeof window !== 'undefined' && !!window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.later(() => {
      const idx = this.appVersionsList.findIndex(v => v.version === version);
      this.appVersionsList = this.appVersionsList.filter(v => v.version !== version);
      this.removingVersion = null;
      this.deleteSuccessMessage = `Version ${version} deleted`;
      this.deleteAuditWarning = auditWritten
        ? ''
        : `Version ${version} was deleted, but the audit record could not be saved. Tell an administrator.`;
      // Focus: next row's Delete (the row now at the same index), else the modal heading; never the body.
      const next = idx >= 0 ? this.appVersionsList.slice(idx).find(v => this.canShowDelete(v)) : undefined;
      this.later(() => {
        if (next && this.focusSelector(`[data-testid="btn-delete-version-${next.version}"]`)) return;
        this.focusHeading();
      });
      this.later(() => {
        this.deleteSuccessMessage = '';
        this.deleteAuditWarning = '';
      }, 6000);
    }, reduced ? 0 : 200);
  }

  private async refreshQuietly(): Promise<void> {
    try {
      this.appVersionsList = await this.releaseService.getAppReleases();
    } catch (err) {
      console.error('Refresh after delete failed:', err);
    }
  }

  private focusById(id: string): void {
    if (typeof document === 'undefined') return;
    document.getElementById(id)?.focus();
  }

  private focusSelector(sel: string): boolean {
    if (typeof document === 'undefined') return false;
    const el = document.querySelector<HTMLElement>(sel);
    el?.focus();
    return !!el;
  }

  private focusHeading(): void {
    this.focusById('appVersionModalTitle');
  }

  onDeleteConfirmFocus(event: FocusEvent): void {
    (event.target as HTMLElement | null)?.scrollIntoView?.({ block: 'center' });
  }

  getSanitizedNote(): SafeHtml {
    return this.releaseNoteContent || '';
  }
}

